/**
 * Your own deck list at the table (batch 3 #17): the list you submitted, with printed costs and how many copies
 * are accounted for. The client never gets your deck from the server (deck order and composition are hidden,
 * CR 108.4), so the list comes from this device, and only for the exact match it was submitted to (audit
 * 2026-10-15, P0: a legend that happens to match is no proof):
 *
 * - When you start something from Play (queue, private room, invite, bot), the deck is held as *pending* for
 *   that start (`rememberPendingDeck`, with your account and how it started: the queue, a room code, the bot).
 * - When this device learns the match id of that start (the queue says matched, the room starts, the bot match
 *   is created), the pending deck becomes an immutable snapshot keyed by account + match id (`bindMatchDeck`); an
 *   existing snapshot is never replaced. The first table view of that match pins your seat to it.
 * - The table asks for account + match id + seat (`submittedDeck`) and gets that snapshot or nothing ("List
 *   unavailable for this match"). The sandbox registers its hot-seat decks per match and seat (`rememberSeatDeck`).
 *
 * "Accounted for" counts only your own non-token cards in zones you can see anyway: hand, base, battlefields
 * (facedown included: you see your own), the chain, the Champion Zone, trash and banishment. The Main Deck is
 * never inspected (not even cards you looked at), so the sheet reveals nothing beyond list − accounted. It is not
 * an exact "left in deck": copies, created cards and cards that changed name can make it inexact.
 */
import type { Catalog } from '@play/catalog';
import { type DeckList, DeckListSchema, type PlayerId, type PlayerView } from '@play/protocol';
import { cardFace } from './cards';

const PENDING_KEY = 'runebound.play.pendingDeck';
const SNAPSHOTS_KEY = 'runebound.play.matchDecks';
/** The old unkeyed store (any legend-matching deck counted); removed on first use. */
const LEGACY_KEY = 'runebound.play.submittedDeck';
/** A pending deck older than this is not bound to a match (a queue left long ago, another session's start). */
const PENDING_TTL_MS = 3 * 60 * 60 * 1000;
/** Snapshots kept on this device (newest first). */
const SNAPSHOT_LIMIT = 24;

/** How a match was started from this device: the queue, a private room (by code) or a bot match. */
export type DeckStart = { kind: 'queue' } | { kind: 'room'; code: string } | { kind: 'bot' };

interface Pending {
  userId: string;
  start: DeckStart;
  deck: DeckList;
  at: number;
}

interface Snapshot {
  userId: string;
  matchId: string;
  /** Pinned by the first table view of the match (`submittedDeck`). */
  seat: PlayerId | null;
  deck: DeckList;
  at: number;
}

function read(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage blocked: the deck list just isn't available at the table.
  }
}

function sameStart(a: DeckStart, b: DeckStart): boolean {
  return a.kind === b.kind && (a.kind !== 'room' || (b.kind === 'room' && a.code === b.code));
}

function isStart(v: unknown): v is DeckStart {
  if (typeof v !== 'object' || v === null) return false;
  const k = (v as { kind?: unknown }).kind;
  return k === 'queue' || k === 'bot' || (k === 'room' && typeof (v as { code?: unknown }).code === 'string');
}

function pending(): Pending | null {
  const v = read(PENDING_KEY);
  if (typeof v !== 'object' || v === null) return null;
  const p = v as Partial<Record<keyof Pending, unknown>>;
  const deck = DeckListSchema.safeParse(p.deck);
  if (typeof p.userId !== 'string' || !isStart(p.start) || typeof p.at !== 'number' || !deck.success) return null;
  return { userId: p.userId, start: p.start, deck: deck.data, at: p.at };
}

function snapshots(): Snapshot[] {
  const v = read(SNAPSHOTS_KEY);
  if (!Array.isArray(v)) return [];
  const out: Snapshot[] = [];
  for (const item of v as unknown[]) {
    if (typeof item !== 'object' || item === null) continue;
    const s = item as Partial<Record<keyof Snapshot, unknown>>;
    const deck = DeckListSchema.safeParse(s.deck);
    if (typeof s.userId !== 'string' || typeof s.matchId !== 'string' || !deck.success) continue;
    const seat = typeof s.seat === 'string' && /^p\d+$/.test(s.seat) ? (s.seat as PlayerId) : null;
    out.push({ userId: s.userId, matchId: s.matchId, seat, deck: deck.data, at: typeof s.at === 'number' ? s.at : 0 });
  }
  return out;
}

/**
 * Holds the deck `userId` is starting something with (`start`) until this device learns the match id
 * (`bindMatchDeck`). A later start replaces it.
 */
export function rememberPendingDeck(userId: string, start: DeckStart, deck: DeckList, now = Date.now()): void {
  write(LEGACY_KEY, null);
  write(PENDING_KEY, { userId, start, deck, at: now } satisfies Pending);
}

/**
 * The match id of a start is known: the pending deck of the same account and start becomes the match's snapshot.
 * Never replaces an existing snapshot (it is immutable once taken). Returns whether a snapshot exists afterwards.
 */
export function bindMatchDeck(userId: string, start: DeckStart, matchId: string, now = Date.now()): boolean {
  const all = snapshots();
  if (all.some((s) => s.userId === userId && s.matchId === matchId)) return true;
  const p = pending();
  if (!p || p.userId !== userId || !sameStart(p.start, start) || now - p.at > PENDING_TTL_MS || now < p.at)
    return false;
  write(PENDING_KEY, null);
  write(SNAPSHOTS_KEY, [{ userId, matchId, seat: null, deck: p.deck, at: now }, ...all].slice(0, SNAPSHOT_LIMIT));
  return true;
}

const seatDecks = new Map<string, DeckList>();

/** Registers a seat's deck for a local (sandbox) match. */
export function rememberSeatDeck(matchId: string, seat: PlayerId, deck: DeckList): void {
  if (seatDecks.size > 32) seatDecks.clear();
  seatDecks.set(`${matchId}:${seat}`, deck);
}

/** Who is asking for which deck: the signed-in account (`null` in the sandbox), the match and the seat. */
export interface DeckKey {
  userId: string | null;
  matchId: string;
  seat: PlayerId;
}

/**
 * The deck `seat` submitted for `matchId`, if this device knows it: a registered sandbox deck, else the snapshot
 * this account took for exactly this match. The first call pins the snapshot to `seat` (the server's view says
 * which seat this account has); a different seat later gets nothing. Never matches by legend or anything else.
 */
export function submittedDeck(key: DeckKey): DeckList | null {
  const local = seatDecks.get(`${key.matchId}:${key.seat}`);
  if (local) return local;
  if (key.userId === null) return null;
  const all = snapshots();
  const snap = all.find((s) => s.userId === key.userId && s.matchId === key.matchId);
  if (!snap) return null;
  if (snap.seat === null) {
    snap.seat = key.seat;
    write(SNAPSHOTS_KEY, all);
  }
  return snap.seat === key.seat ? snap.deck : null;
}

/** Zones whose cards count as accounted for (never a deck, never runes or the legend). */
const ACCOUNTED_ZONES: ReadonlySet<string> = new Set([
  'hand',
  'base',
  'battlefield',
  'facedown',
  'chain',
  'champion',
  'trash',
  'banishment',
]);

/** How many of `player`'s own (non-token) cards of each card id are in an accounted-for zone of `view`. */
export function accountedCounts(view: PlayerView, player: PlayerId): Map<string, number> {
  const out = new Map<string, number>();
  for (const o of Object.values(view.objects)) {
    if (o.owner !== player || o.isToken || !o.card || o.category !== 'card') continue;
    if (!ACCOUNTED_ZONES.has(o.zone.kind)) continue;
    out.set(o.card.cardId, (out.get(o.card.cardId) ?? 0) + 1);
  }
  return out;
}

/** One line of the reference. */
export interface DeckRefRow {
  cardId: string;
  printingId: string | null;
  name: string;
  type: string;
  domains: readonly string[];
  energy: number | null;
  power: number | null;
  quantity: number;
  /** Copies accounted for (in hand, on the board, chain, Champion Zone, trash, banishment), 0..quantity. */
  accounted: number;
}

/** The reference for a deck: champion, main deck (by energy, then name) and runes. */
export interface DeckReference {
  legend: DeckRefRow;
  champion: DeckRefRow | null;
  main: DeckRefRow[];
  runes: DeckRefRow[];
  /** Main deck cards in the list, the chosen champion included. */
  mainTotal: number;
  /** Of `mainTotal`, how many are accounted for. */
  mainAccounted: number;
}

function row(
  catalog: Catalog | null,
  entry: { cardId: string; printingId: string | null },
  quantity: number,
  accounted: number,
): DeckRefRow {
  const face = cardFace(catalog, { cardId: entry.cardId, printingId: entry.printingId });
  return {
    cardId: entry.cardId,
    printingId: entry.printingId,
    name: face.name,
    type: face.type,
    domains: face.domains.filter((d) => d !== 'Colorless'),
    energy: face.energy,
    power: face.power,
    quantity,
    accounted: Math.max(0, Math.min(quantity, accounted)),
  };
}

/**
 * Builds the reference. The Chosen Champion is its own slot (it is not repeated in `main`, CR 103.3.a): its copy
 * is counted first, the rest of that name go to the main-deck line (CR 103.3.b.1).
 */
export function deckReference(
  deck: DeckList,
  catalog: Catalog | null,
  accounted: ReadonlyMap<string, number>,
): DeckReference {
  const left = new Map(accounted);
  const take = (cardId: string, n: number): number => {
    const have = left.get(cardId) ?? 0;
    const used = Math.min(have, n);
    left.set(cardId, have - used);
    return used;
  };
  const champion = deck.champion ? row(catalog, deck.champion, 1, take(deck.champion.cardId, 1)) : null;
  const merged = new Map<string, { cardId: string; printingId: string | null; quantity: number }>();
  for (const e of deck.main) {
    const prev = merged.get(e.cardId);
    if (prev) prev.quantity += e.quantity;
    else merged.set(e.cardId, { cardId: e.cardId, printingId: e.printingId, quantity: e.quantity });
  }
  const main = [...merged.values()]
    .map((e) => row(catalog, e, e.quantity, take(e.cardId, e.quantity)))
    .sort((a, b) => (a.energy ?? 99) - (b.energy ?? 99) || a.name.localeCompare(b.name));
  const runes = deck.runes.map((e) => row(catalog, e, e.quantity, 0)).sort((a, b) => a.name.localeCompare(b.name));
  return {
    legend: row(catalog, deck.legend, 1, 0),
    champion,
    main,
    runes,
    mainTotal: main.reduce((n, r) => n + r.quantity, 0) + (champion ? 1 : 0),
    mainAccounted: main.reduce((n, r) => n + r.accounted, 0) + (champion?.accounted ?? 0),
  };
}

/** The rows matching a search (name, type or domain; every word must match). */
export function searchDeckRows(rows: readonly DeckRefRow[], query: string): DeckRefRow[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...rows];
  return rows.filter((r) => {
    const hay = [r.name, r.type, ...r.domains].join(' ').toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}
